import React from "react";
// Common Components
import { DynamicMultiSelectDropdown, TextInput } from "src/Components";
import TableTemplate from "src/Tables/TableTemplate/tableTemplate";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import { getAssetClassAdditional } from "./AssetClassAdditional";
// Requests
import {
  getAssetClassInstrumentsDropDown,
  getAssetClassTable,
  getAssetTypesFilterDropDown,
} from "src/Requests/assetClass";
// Types
import type {
  IAssetClassSearchParams,
  IAssetClassTableReport,
} from "src/Interfaces/assetClass";
import { NO_ASSET_CLASS_ID } from "src/Interfaces/assetClass";
import { useDebouncer } from "src/Hooks/useDebouncer";

const AssetClass: React.FC = () => {
  const [searchText, setSearchText] = React.useState("");
  // kept as string[] to match the dropdown option ids; mapped to numbers for the request
  const [assetTypes, setAssetTypes] = React.useState<string[]>([]);
  const [instrumentIds, setInstrumentIds] = React.useState<string[]>([]);
  const [count, setCount] = React.useState<number | undefined>(undefined);

  // rows of the current page, used to map selected row ids -> ISIN for group edit
  const rowsRef = React.useRef<IAssetClassTableReport[]>([]);

  const debouncedSearch = useDebouncer(searchText, 400);

  const searchParameters = React.useMemo<IAssetClassSearchParams>(
    () => ({
      searchText: debouncedSearch || null,
      // keep the "بدون طبقه" sentinel string as-is; only real ids are numeric
      assetTypes: assetTypes.map((t) =>
        t === NO_ASSET_CLASS_ID ? t : Number(t),
      ),
      instrumentIds,
    }),
    [debouncedSearch, assetTypes, instrumentIds],
  );

  const resolveInstrumentIds = React.useCallback(
    (selectedRowsIds: number[]): string[] =>
      rowsRef.current
        .filter((row) => selectedRowsIds.includes(row.id))
        .map((row) => row.instrumentId)
        .filter((isin): isin is string => !!isin),
    [],
  );

  const additional = React.useMemo(
    () => getAssetClassAdditional(resolveInstrumentIds),
    [resolveInstrumentIds],
  );

  return (
    <div className="p-7">
      <div className="flex items-start justify-start gap-6">
        <div className="w-64">
          <TextInput
            name="searchText"
            label="جست و جو"
            extraLabel
            onChange={(_, value) => setSearchText(value)}
            value={searchText}
          />
        </div>
        <div className="w-64">
          <DynamicMultiSelectDropdown
            dropdown={{
              name: "assetTypes",
              placeholder: "طبقه دارایی",
              extraLabel: true,
            }}
            getData={getAssetTypesFilterDropDown}
            onChange={(_, value) => setAssetTypes(value)}
            value={assetTypes}
            withTagBox
          />
        </div>
        <div className="w-64">
          <DynamicMultiSelectDropdown
            dropdown={{
              name: "instrumentIds",
              placeholder: "نماد",
              extraLabel: true,
            }}
            getData={getAssetClassInstrumentsDropDown}
            onChange={(_, value) => setInstrumentIds(value)}
            value={instrumentIds}
            withTagBox
          />
        </div>
        <div className="whitespace-nowrap text-sm font-bold mr-auto">
          تعداد: {count ?? "....."}
        </div>
      </div>
      <div className="w-min mx-auto">
        <TableTemplate<IAssetClassSearchParams, IAssetClassTableReport>
          getDataTable={getAssetClassTable}
          searchParameters={searchParameters}
          pagesize={100}
          showPageSize
          withSort
          onResponse={(response) => {
            rowsRef.current = response.report || [];
            setCount(response.count);
          }}
          TableUI={({ reports, onAction, sortingType, setSortingType }) => (
            <CommonTableUI
              reports={reports}
              onAction={onAction}
              AdditionalBody={additional}
              withSelect
              sortingType={sortingType}
              setSortingType={setSortingType}
              tableBoxClassName="!mx-0 !max-h-[600px]"
            />
          )}
        />
      </div>
    </div>
  );
};

export default AssetClass;
